// The sticky 3D stage of the home page story. One globe stays in view while the
// chapters scroll past; each chapter turns it to a new view (see globe-views.ts)
// and swaps the small UI piece at the top. The globe is large and cut by the
// stage's bottom edge, with two callouts beside it (owner restyle 2026-10-11).
//
// It keeps the same contract as the hero stage (use-globe-loop.ts): a static CSS
// glow paints first, the scene loads only when the stage nears the viewport, it
// pauses off screen, shows one still frame for prefers-reduced-motion, and shares
// the page's pause switch. The stage is decorative and aria-hidden: every
// chapter's text carries the same information, and on small screens the pieces
// appear inline in the chapters instead.
'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { GlobePauseButton } from './globe-pause-button'
import { useGlobePaused } from './globe-pause'
import { viewForChapter } from './globe-views'
import { useGlobeLoop } from './use-globe-loop'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const STORY_START = 15.5
/** Bottom edge of the UI piece (top-5 + 176 px), in CSS pixels. */
const PIECE_BOTTOM = 196

export function ScrollGlobeStage({ active, pieces }: { active: number; pieces: ReactNode[] }) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const activeRef = useRef(active)
  useEffect(() => {
    activeRef.current = active
  }, [active])
  const [paused, setPaused] = useGlobePaused()
  const { live, renderStill } = useGlobeLoop({
    hostRef,
    storyStart: STORY_START,
    trigger: 'near',
    interactive: true,
    getView: () => viewForChapter(activeRef.current),
    computeLayout: (hr) => {
      // Big and cropped at the bottom; the top of the globe starts below the UI piece.
      // Side margins stay; only the bottom edge cuts the globe.
      const radius = Math.min(hr.width * 0.42, hr.height * 0.5)
      return { cx: hr.width / 2, cy: Math.max(PIECE_BOTTOM + 20 + radius, hr.height * 0.78), radius }
    },
  })

  // Reduced motion draws one still frame, so it must be redrawn when the chapter changes.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) renderStill()
  }, [active, renderStill])

  return (
    <div className="dark relative h-full">
      <div aria-hidden="true" className="dark relative h-full overflow-hidden rounded-frame border border-border bg-brand-dark text-foreground">
        <div
          ref={hostRef}
          // Static glow: paints first and stays as the fallback if WebGL is missing.
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 42% at 50% 40%, color-mix(in oklab, var(--primary) 16%, transparent), transparent 70%)',
          }}
          // Grab to spin; on touch, vertical swipes still scroll the page.
          className="absolute inset-0 cursor-grab touch-pan-y select-none overflow-hidden active:cursor-grabbing"
        />
        <div className="absolute inset-x-5 top-5 z-10 h-[176px]">
          {pieces.map((piece, i) => (
            <div
              key={i}
              className={`absolute inset-0 overflow-hidden rounded-lg border border-border bg-background transition-opacity duration-200 motion-reduce:transition-none ${
                i === active ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {piece}
            </div>
          ))}
        </div>
        <p className="absolute left-4 top-[236px] z-10 max-w-[12rem] rounded-sm border border-dashed border-primary bg-background px-3 py-2 text-sm text-primary">
          {MONITOR_LOCATIONS.globe.label}
        </p>
        <p className="absolute right-4 top-[300px] z-10 max-w-[12rem] rounded-sm border border-dashed border-primary bg-background px-3 py-2 text-sm text-primary">
          {MONITOR_LOCATIONS.globe.confirm}
        </p>
      </div>
      {/* Outside the aria-hidden stage, so assistive tech can reach it. */}
      {live && <GlobePauseButton paused={paused} onToggle={() => setPaused(!paused)} className="absolute bottom-3 right-3 z-20" />}
    </div>
  )
}
